import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { MessagesSquare, Loader2, Lock, Send, ShieldQuestion } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STAFF_ROLES = ["deal_manager", "admin", "super_admin", "compliance_director", "senior_verification_manager", "compliance_reviewer"];
const CHANNELS = [
  ["general", "General Deal Operations"], ["legal", "Legal"], ["compliance", "Compliance"],
  ["inspection", "Inspection"], ["logistics", "Logistics"], ["insurance", "Insurance"],
  ["customs", "Customs"], ["accounting", "Accounting & Tax"], ["financing", "Financing"],
  ["escrow", "Escrow"], ["mediation", "Mediation & Arbitration"],
];

export default function ChannelsPanel({ deal }) {
  const { user } = useAuth();
  const isStaff = STAFF_ROLES.includes(user?.role);
  const [channel, setChannel] = useState("general");
  const [internal, setInternal] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState(false);

  const url = internal ? `/deals/${deal.deal_id}/internal-notes`
    : channel === "general" ? `/deals/${deal.deal_id}/messages`
    : `/deals/${deal.deal_id}/channels/${channel}/messages`;

  const load = useCallback(async () => {
    setLoading(true); setDenied(false);
    try {
      const { data } = await api.get(url);
      setMsgs(data.messages || data.notes || []);
    } catch (err) {
      if (err.response?.status === 403) setDenied(true);
      else toast.error(formatApiError(err.response?.data?.detail));
      setMsgs([]);
    } finally { setLoading(false); }
  }, [url]);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post(url, { body });
      setBody(""); await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-6 border-t border-slate-200 pt-5" data-testid="channels-panel">
      <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700"><MessagesSquare size={15} /> Secure channels</h4>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select data-testid="channel-select" value={channel} disabled={internal}
          onChange={(e) => setChannel(e.target.value)} className="h-9 rounded-md border border-slate-300 px-3 text-sm disabled:opacity-50">
          {CHANNELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {isStaff && (
          <label className="flex items-center gap-1.5 text-xs text-slate-600" data-testid="internal-notes-toggle">
            <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />
            Internal staff notes
          </label>
        )}
      </div>

      {denied ? (
        <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500 flex items-center gap-2" data-testid="channel-denied">
          <Lock size={15} /> You are not authorized to access this channel.
        </div>
      ) : loading ? (
        <div className="py-6 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>
      ) : (
        <>
          <div className="max-h-72 space-y-2 overflow-y-auto rounded-lg border border-slate-200 p-3" data-testid="channel-messages">
            {msgs.length === 0 && <p className="text-sm text-slate-400">No messages in this channel yet.</p>}
            {msgs.map((m) => (
              <div key={m.message_id} data-testid={`channel-msg-${m.message_id}`} className={`rounded-md p-2.5 text-sm ${internal || m.channel === "internal" ? "bg-amber-50 border border-amber-100" : "bg-slate-50"}`}>
                <div className="mb-0.5 flex items-center justify-between text-[11px] text-slate-400">
                  <span>{m.author_role || "user"}{(internal || m.channel === "internal") && <span className="ml-1 rounded bg-amber-200 px-1 text-amber-800">INTERNAL</span>}</span>
                  <span>{new Date(m.created_at).toLocaleString()}</span>
                </div>
                <p className="text-slate-700">{m.body}</p>
                {m.ai_flags?.length > 0 && <p className="mt-1 text-[11px] text-amber-700 flex items-center gap-1"><ShieldQuestion size={11} /> {m.ai_flags.map((f) => f.code).join(", ")}</p>}
              </div>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <input data-testid="channel-input" value={body} onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Write a message… (attachments reference the Vault)"
              className="h-9 flex-1 rounded-md border border-slate-300 px-3 text-sm" />
            <button data-testid="channel-send-btn" onClick={send} disabled={busy}
              className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[#2563EB] px-3 text-sm font-medium text-white disabled:opacity-60">
              <Send size={13} /> Send
            </button>
          </div>
          <p className="mt-1.5 text-[11px] text-slate-400">Sensitive identifiers are blocked from messages — use the Document Vault. History is append-only & audited.</p>
        </>
      )}
    </div>
  );
}
